import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { Visibility, VisibilityOff, ContentCopy, Check } from "@mui/icons-material";
import {
  getMemberTasks,
  getOneEmployee,
} from "../../redux/actions/authActions";
import Sidebar from "../../components/sidebar/Sidebar";
import SectionHeader from "../../components/sectionHeader/SectionHeader";
import MainSection from "../../components/mainLayout/Main";
import { FaTasks, FaUserTie } from "react-icons/fa";
import { Tag, Popover, Tooltip as AntTooltip } from "antd";

function ViewEmployee() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams(); // This is the memberId/employeeId

  // Selectors updated to match your Redux state keys
  const employee = useSelector((state) => state.auth.employee);
  const { member_tasks } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    clientsAssigned: 0,
    tasksAssigned: 0,
  });
  const [employeeTasks, setEmployeeTasks] = useState([]);
  const [completionProgress, setCompletionProgress] = useState(0);
  const [showDefaultPassword, setShowDefaultPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        try {
          await dispatch(getOneEmployee(id));
          await dispatch(getMemberTasks(id));
        } catch (err) {
          console.error("Error fetching employee data:", err);
        }
      };
      fetchData();
    }
  }, [dispatch, id]);

  useEffect(() => {
    // Ensure we have both employee info and the task list
    if (employee && member_tasks) {
      // Process tasks specifically assigned to this member ID
      const empTasks = member_tasks.filter(
        (task) => task.assignees && task.assignees.includes(id),
      );

      const completedTasks = empTasks.filter(
        (t) => t.status === "Completed",
      ).length;

      // Extract unique client names/IDs
      const uniqueClients = Array.from(
        new Set(empTasks.map((task) => task.client?._id).filter(Boolean)),
      );

      const progress =
        empTasks.length > 0
          ? Math.round((completedTasks / empTasks.length) * 100)
          : 0;

      // Format rows for DataGrid (ensuring unique 'id' field)
      const formattedTasks = empTasks.map((task) => ({
        ...task,
        id: task._id, // DataGrid requires an 'id' property
        taskIdDisplay: task.id || task._id, // Human-readable ID for display
        clientName: task.client?.name || "N/A",
        serviceName: task.service?.name || "N/A",
      }));

      setFormData({
        name: employee.name || "",
        email: employee.email || "",
        phoneNumber: employee.phoneNumber || "",
        clientsAssigned: uniqueClients.length,
        tasksAssigned: empTasks.length,
      });

      setEmployeeTasks(formattedTasks);
      setCompletionProgress(progress);
    }
  }, [employee, member_tasks, id]);

  const handleEdit = () => {
    navigate(`/team-management/edit-employee/${id}`);
  };

  const handleViewTask = (taskId) => {
    const task = employeeTasks.find((t) => t.id === taskId);
    if (task && task.client?._id) {
      // Navigates to the view path you mentioned earlier
      navigate(
        `/team-management/member-task/view?clientId=${task.client._id}&taskId=${taskId}`,
      );
    } else {
      console.error("Client or Task ID is missing.");
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Pending":
        return { color: "#f97316", fontWeight: "bold" };
      case "In Progress":
        return { color: "#eab308", fontWeight: "bold" };
      case "Completed":
        return { color: "#16a34a", fontWeight: "bold" };
      case "Upcoming":
        return { color: "#2563eb", fontWeight: "bold" };
      default:
        return {};
    }
  };

  const columns = [
    { field: "taskIdDisplay", headerName: "Task ID", width: 150 },
    { field: "serviceName", headerName: "Task Name", width: 220 },
    {
      field: "state",
      headerName: "State",
      width: 170,
      renderCell: (params) => {
        const row = params.row;
        let states = [];
        if (row.locations && Array.isArray(row.locations) && row.locations.length > 0) {
          states = [...new Set(row.locations.map((l) => l.state?.trim()).filter(Boolean))];
        } else if (row.state?.trim()) {
          states = [row.state.trim()];
        }

        if (states.length === 0) return <span>-</span>;
        if (states.length <= 2) {
          return (
            <div className="flex flex-wrap gap-1 items-center h-full py-1">
              {states.map((st, i) => (
                <Tag key={i} color="blue" className="!mr-1 !mb-0 text-xs">
                  {st}
                </Tag>
              ))}
            </div>
          );
        }

        return (
          <div className="flex flex-wrap gap-1 items-center h-full py-1">
            {states.slice(0, 2).map((st, i) => (
              <Tag key={i} color="blue" className="!mr-1 !mb-0 text-xs">
                {st}
              </Tag>
            ))}
            <Popover
              trigger={["hover", "click"]}
              placement="bottom"
              content={
                <div style={{ maxWidth: 260 }}>
                  <div className="font-bold text-xs text-gray-700 pb-1.5 mb-1.5 border-b border-gray-100">
                    All States ({states.length})
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-48 overflow-y-auto">
                    {states.map((st, i) => (
                      <Tag key={i} color="blue" className="text-xs">
                        {st}
                      </Tag>
                    ))}
                  </div>
                </div>
              }
            >
              <Tag color="processing" className="!mr-0 !mb-0 text-xs font-semibold cursor-pointer">
                +{states.length - 2} more
              </Tag>
            </Popover>
          </div>
        );
      },
    },
    {
      field: "location",
      headerName: "Location / Cities",
      width: 220,
      renderCell: (params) => {
        const row = params.row;
        let validBranches = [];
        if (row.locations && Array.isArray(row.locations) && row.locations.length > 0) {
          validBranches = row.locations.filter((l) => l.location?.trim() || l.state?.trim());
        } else if (row.location?.trim() || row.state?.trim()) {
          validBranches = [{ location: row.location?.trim() || "", state: row.state?.trim() || "" }];
        }

        if (validBranches.length === 0) return <span>-</span>;
        if (validBranches.length <= 2) {
          return (
            <div className="flex flex-wrap gap-1 items-center h-full py-1">
              {validBranches.map((l, i) => (
                <AntTooltip
                  key={i}
                  title={l.state ? `${l.location || 'Branch'} (${l.state})` : (l.location || l.state)}
                >
                  <Tag key={i} color="cyan" className="!mr-1 !mb-0 text-xs">
                    {l.location || l.state}
                  </Tag>
                </AntTooltip>
              ))}
            </div>
          );
        }

        const grouped = {};
        validBranches.forEach((l) => {
          const st = l.state?.trim() || "Other";
          const loc = l.location?.trim() || "All Branches";
          if (!grouped[st]) grouped[st] = [];
          if (!grouped[st].includes(loc)) grouped[st].push(loc);
        });

        return (
          <div className="flex flex-wrap gap-1 items-center h-full py-1">
            {validBranches.slice(0, 2).map((l, i) => (
              <AntTooltip
                key={i}
                title={l.state ? `${l.location || 'Branch'} (${l.state})` : (l.location || l.state)}
              >
                <Tag color="cyan" className="!mr-1 !mb-0 text-xs">
                  {l.location || l.state}
                </Tag>
              </AntTooltip>
            ))}
            <Popover
              trigger={["hover", "click"]}
              placement="bottom"
              content={
                <div style={{ minWidth: 240, maxWidth: 300 }}>
                  <div className="font-bold text-xs text-gray-700 pb-1.5 mb-1.5 border-b border-gray-100">
                    Cities & Branches ({validBranches.length})
                  </div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {Object.entries(grouped).map(([st, cList], idx) => (
                      <div key={idx} className="bg-gray-50 p-2 rounded border border-gray-100">
                        <div className="text-[11px] font-bold text-blue-700 uppercase tracking-wide mb-1">
                          {st} ({cList.length})
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {cList.map((city, cIdx) => (
                            <Tag key={cIdx} color="default" className="text-xs bg-white">
                              {city}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              }
            >
              <Tag color="geekblue" className="!mr-0 !mb-0 text-xs font-bold cursor-pointer">
                +{validBranches.length - 2} more
              </Tag>
            </Popover>
          </div>
        );
      },
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography
          style={{
            color:
              new Date(params.value) < new Date() &&
              params.row.status !== "Completed"
                ? "#dc2626"
                : "#111827",
            fontWeight: "500",
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          {params.value ? new Date(params.value).toDateString() : "N/A"}
        </Typography>
      ),
    },
    { field: "clientName", headerName: "Client Name", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <div style={getStatusStyle(params.value)}>{params.value}</div>
      ),
    },
    {
      field: "view",
      headerName: "View",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View Task">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleViewTask(params.row.id)}
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const cleanName = (formData.name || "").replace(/\s+/g, "");
  const last5 =
    formData.phoneNumber && formData.phoneNumber.length >= 5
      ? formData.phoneNumber.slice(-5)
      : formData.phoneNumber || "";
  const defaultPassword = cleanName + last5;

  return (
    <>
      <Sidebar />
      <MainSection>
        <div className="max-w-6xl mx-auto py-2 px-2 pb-16 space-y-6 overflow-x-hidden">
          <SectionHeader title="View Employee" />

          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-800">
                  Employee Details
                </h2>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={handleEdit}
                  className="px-4 py-2 rounded-full text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  Edit Employee
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                  Name
                </label>
                <p className="text-lg font-medium text-gray-900">
                  {formData.name}
                </p>
              </div>
              <div>
                <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                  Email Address
                </label>
                <p className="text-lg font-medium text-gray-900">
                  {formData.email}
                </p>
              </div>
              <div>
                <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                  Phone Number
                </label>
                <p className="text-lg font-medium text-gray-900">
                  {formData.phoneNumber}
                </p>
              </div>
              <div>
                <label className="block mb-1 text-xs font-semibold text-gray-500 uppercase">
                  Default Password
                </label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-lg font-mono font-medium text-gray-900 select-all">
                    {showDefaultPassword
                      ? defaultPassword || "N/A"
                      : defaultPassword
                      ? "••••••••"
                      : "N/A"}
                  </p>
                  {defaultPassword && (
                    <div className="flex items-center space-x-1">
                      <Tooltip
                        title={
                          showDefaultPassword
                            ? "Hide default password"
                            : "Show default password"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowDefaultPassword((prev) => !prev)
                          }
                          sx={{ p: 0.5 }}
                        >
                          {showDefaultPassword ? (
                            <VisibilityOff sx={{ fontSize: 18 }} />
                          ) : (
                            <Visibility sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={copied ? "Copied!" : "Copy password"}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(defaultPassword);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          {copied ? (
                            <Check sx={{ fontSize: 18, color: "green" }} />
                          ) : (
                            <ContentCopy sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Compact Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card 1: Total Tasks */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <FaTasks className="text-lg text-blue-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900 leading-tight">{formData.tasksAssigned || 0}</p>
                <p className="text-gray-500 text-xs font-medium">Total Tasks</p>
              </div>
            </div>

            {/* Card 2: Clients Assigned */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                <FaUserTie className="text-lg text-emerald-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900 leading-tight">{formData.clientsAssigned || 0}</p>
                <p className="text-gray-500 text-xs font-medium">Clients Assigned</p>
              </div>
            </div>

            {/* Card 3: Completion Progress */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col justify-center">
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs font-semibold text-gray-700">Completion Progress</span>
                <span className="text-xs font-bold text-blue-600">{completionProgress}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${completionProgress}%` }}
                />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {completionProgress}% completed
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Assigned Tasks
            </h3>
            <div className="w-full overflow-x-auto">
              <DataGrid
                rows={employeeTasks}
                columns={columns}
                pageSizeOptions={[5, 8, 10, 25]}
                disableRowSelectionOnClick
                initialState={{
                  pagination: { paginationModel: { pageSize: 8, page: 0 } },
                }}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f9fafb",
                  },
                }}
              />
            </div>
          </div>
        </div>
      </MainSection>
    </>
  );
}

export default ViewEmployee;
